import React from 'react';

import { HStack } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { AppStoreBadge } from 'components/app-store-badge';
import { GooglePlayBadge } from 'components/google-play-badge';
import PictureComponent from 'components/picture';
import { useIsDesktopLargeBreakpoint } from 'hooks/use-media-query';
import { isNative } from 'utils/environment';

import {
  BadgeLink,
  BadgesContainer,
  Description,
  Disclaimer,
  Header,
  HeaderContainer,
  ImageDesktop,
  ImageMobile,
  LayoutContainerBackground,
  MainImageContainer,
  Picture,
  PictureMobile,
  Section,
  TextHalf,
} from './styles';
import { IAppDownloadProps } from './types';

export const AppDownloadSection: React.FC<IAppDownloadProps> = ({
  item: appDownloadSection,
  loading,
}) => {
  const { formatMessage } = useIntl();
  const isDesktop = useIsDesktopLargeBreakpoint();
  const {
    androidDownloadImage,
    androidDownloadLink,
    iOSDownloadImage,
    iOSDownloadLink,
    huaweiDownloadImage,
    huaweiDownloadLink,
    headerSuperText,
    header,
    copy,
    primaryImage,
    primaryImageDesktop,
  } = appDownloadSection || {};

  if (isNative || loading) {
    return null;
  }

  const image = isDesktop
    ? primaryImageDesktop?.locale ?? primaryImage?.locale
    : primaryImage?.locale;

  const iosDownloadApp = formatMessage({ id: 'iosDownloadApp' });
  const androidDownloadApp = formatMessage({ id: 'androidDownloadApp' });
  const huaweiDownloadApp = formatMessage({ id: 'huaweiDownloadApp' });

  return (
    <LayoutContainerBackground>
      <Section>
        <ImageDesktop>
          <MainImageContainer>
            {image && <Picture image={image} alt="" objectFitContain />}
          </MainImageContainer>
        </ImageDesktop>
        <TextHalf>
          <HStack width="full">
            <ImageMobile>
              {primaryImage?.locale && (
                <PictureMobile image={primaryImage?.locale} alt="" objectFitContain />
              )}
            </ImageMobile>
            <HeaderContainer>
              <Header>{headerSuperText?.locale}</Header>
              <Header>{header?.locale}</Header>
            </HeaderContainer>
          </HStack>
          {copy?.locale && <Description>{copy?.locale}</Description>}
          <BadgesContainer>
            <BadgeLink
              data-testid="iOS-download-link"
              href={iOSDownloadLink || ''}
              title={iosDownloadApp}
            >
              {iOSDownloadImage?.locale ? (
                <PictureComponent image={iOSDownloadImage.locale} alt={iosDownloadApp} />
              ) : (
                <AppStoreBadge width="100%" alt={iosDownloadApp} />
              )}
            </BadgeLink>
            <BadgeLink
              data-testid="android-download-link"
              href={androidDownloadLink || ''}
              title={androidDownloadApp}
            >
              {androidDownloadImage?.locale ? (
                <PictureComponent image={androidDownloadImage?.locale} alt={androidDownloadApp} />
              ) : (
                <GooglePlayBadge width="100%" alt={androidDownloadApp} />
              )}
            </BadgeLink>
            {huaweiDownloadImage?.locale ? (
              <BadgeLink
                data-testid="huawei-download-link"
                href={huaweiDownloadLink || ''}
                title={huaweiDownloadApp}
              >
                <PictureComponent image={huaweiDownloadImage?.locale} alt={huaweiDownloadApp} />
              </BadgeLink>
            ) : null}
          </BadgesContainer>
          <Disclaimer>{formatMessage({ id: 'phoneAppStoresDisclaimers' })}</Disclaimer>
        </TextHalf>
      </Section>
    </LayoutContainerBackground>
  );
};
